/**
 * The platform's plan files, read as they arrive (`GET …/plans/{id}/export?kind=`, a streamed RFC 4180 CSV with a
 * byte-order mark, one column per field of the page's item). A plan's file never is one string: the pieces are
 * read once and each is turned into whole records as it completes (ADR 9073). A file that comes up short
 * is never handed over: the platform states the rows it holds before the first, and a different count is an
 * `ApiIncompleteFileError`.
 *
 * The CSV reader itself is the shared `CsvRowReader` of `@/api`.
 */

import { ApiIncompleteFileError, CsvRowReader, CSV_FORMULA_GUARD, type CsvProgress, type CsvStream } from '@/api';

/** A record of the file by field name, its values typed as the page's JSON would be: empty is `null`, `true`/`false` are booleans. */
export type PlanRecord = Readonly<Record<string, string | boolean | null>>;

export class PlanFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlanFileError';
  }
}


function valueOf(cell: string): string | boolean | null {
  if (cell === '') return null;
  if (cell === 'true') return true;
  if (cell === 'false') return false;
  return cell.replace(CSV_FORMULA_GUARD, '');
}

/**
 * The file's records, one at a time — none is kept. Progress is the records read against the rows the platform stated;
 * at the end, a count other than the stated one is an `ApiIncompleteFileError`, so a short file is never written.
 */
export async function* streamedRecords(file: CsvStream, onProgress?: CsvProgress): AsyncGenerator<PlanRecord> {
  const reader = new CsvRowReader((message) => new PlanFileError(message));
  let header: readonly string[] | null = null;
  let count = 0;
  onProgress?.(0, file.statedRows);
  let finished = false;
  try {
    const pieces = file.pieces[Symbol.asyncIterator]();
    for (;;) {
      const next = await pieces.next();
      const rows = next.done === true ? reader.end() : reader.push(next.value);
      for (const fields of rows) {
        if (header === null) { header = fields; continue; }
        count += 1;
        const record: Record<string, string | boolean | null> = {};
        header.forEach((name, index) => { record[name] = valueOf(fields[index] ?? ''); });
        yield record;
      }
      onProgress?.(file.statedRows === null ? count : Math.min(count, file.statedRows), file.statedRows);
      if (next.done === true) break;
    }
    finished = true;
  } finally {
    if (!finished) await file.cancel();
  }
  if (header === null) throw new PlanFileError('the file is empty');
  if (file.statedRows !== null && count !== file.statedRows) throw new ApiIncompleteFileError(file.status, count, file.statedRows);
}
